import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import type { ProductSearchResult } from '../../services/products/products.service';
import { getFirstProductImage } from '../../utils/product-images';
import { capitalizeProductName } from '../../utils/capitalize';
import { Check, X, ShoppingCart, AlertCircle, Package } from 'lucide-react';

interface SearchResultsProps {
  results: ProductSearchResult[];
  isLoading: boolean;
  query: string;
  isVisible: boolean;
  onClose: () => void;
  onAddToCart?: (product: ProductSearchResult) => void;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  isLoading,
  query,
  isVisible,
  onClose,
  onAddToCart
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleProductClick = (product: ProductSearchResult) => {
    // Verificar si el usuario está logueado y navegar a la ruta correspondiente
    const isAuthenticated = localStorage.getItem('accessToken') && localStorage.getItem('userData');
    
    if (isAuthenticated) {
      // Usuario logueado: ir a la sección de compras del cliente
      navigate(`/cliente/compras?search=${encodeURIComponent(product.nombre)}`);
    } else {
      // Usuario no logueado: ir a la página pública de productos
      navigate(`/productos?search=${encodeURIComponent(product.nombre)}`);
    }
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-2 z-50">
      <div className={`rounded-lg border shadow-xl max-h-96 overflow-y-auto ${
        theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`sticky top-0 px-4 py-3 border-b flex justify-between items-center ${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}>
          <h3 className={`font-medium ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Resultados para "{query}"
          </h3>
          <button
            onClick={onClose}
            className={`p-1 rounded-md transition-colors ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
              <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                Buscando medicamentos...
              </span>
            </div>
          </div>
        )}

        {/* No results */}
        {!isLoading && results.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <AlertCircle className={`w-12 h-12 mb-3 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }`} />
            <p className={`text-center font-medium ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              No encontramos medicamentos con "{query}"
            </p>
            <p className={`text-sm text-center mt-1 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              Verifica la ortografía o intenta con otro término
            </p>
          </div>
        )}

        {/* Results */}
        {!isLoading && results.length > 0 && (
          <div className="py-2">
            {results.map((product) => (
              <div
                key={product.id}
                onClick={() => handleProductClick(product)}
                className={`px-4 py-3 hover:bg-opacity-50 transition-colors cursor-pointer border-b last:border-b-0 ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 border-gray-700'
                    : 'hover:bg-gray-50 border-gray-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      {/* Product Image */}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                      }`}>
                        {getFirstProductImage(product.imagenes) ? (
                          <img
                            src={getFirstProductImage(product.imagenes)}
                            alt={product.nombre}
                            className="w-8 h-8 object-cover rounded"
                          />
                        ) : (
                          <Package className={`w-6 h-6 ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                          }`} />
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1">
                        <h4 className={`font-medium text-sm ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {capitalizeProductName(product.nombre)}
                        </h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            theme === 'dark'
                              ? 'bg-blue-900/50 text-blue-300'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {product.categoria}
                          </span>
                          {product.requiereReceta && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              theme === 'dark'
                                ? 'bg-orange-900/50 text-orange-300'
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              Receta médica
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Price and Stock */}
                  <div className="text-right ml-4">
                    <p className={`font-bold ${
                      theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
                    }`}>
                      ${(product.presentaciones?.[0]?.precio || 0).toLocaleString()}
                    </p>
                    <div className="flex items-center justify-end space-x-1 mt-1">
                      {(product.presentaciones?.[0]?.stock || 0) > 0 ? (
                        <>
                          <Check className="w-3 h-3 text-green-500" />
                          <span className={`text-xs ${
                            theme === 'dark' ? 'text-green-400' : 'text-green-600'
                          }`}>
                            Disponible ({(product.presentaciones?.[0]?.stock || 0)})
                          </span>
                        </>
                      ) : (
                        <>
                          <X className="w-3 h-3 text-red-500" />
                          <span className={`text-xs ${
                            theme === 'dark' ? 'text-red-400' : 'text-red-600'
                          }`}>
                            Sin stock
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Add to Cart Button */}
                  {(product.presentaciones?.[0]?.stock || 0) > 0 && onAddToCart && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddToCart(product);
                      }}
                      className={`ml-3 p-2 rounded-lg transition-colors ${
                        theme === 'dark'
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                      }`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {results.length > 0 && (
          <div className={`px-4 py-3 border-t text-center ${
            theme === 'dark'
              ? 'border-gray-700 bg-gray-800'
              : 'border-gray-200 bg-gray-50'
          }`}>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {results.length} producto{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;